import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { errorCatch } from './errorCatcher.reducer';
import countries from '../../public/static/countries.json';

export type Country = {
  name: string;
  id: string;
  iso2: string;
  iso3: string;
  order: number;
};

export type CountriesDataType = {
  '@odata.count': number;
  value: Country[];
};

export type RegionsDataType = {
  '@odata.count': number;
  value: Region[];
};

export type Region = {
  countryId: string;
  name: string;
  id: string;
  order: number;
};

export type InitState = {
  countries: {
    status: string;
    data: Country[];
  };
  regions: {
    status: string;
    data: Region[];
  };
};

export const initialState = {
  countries: {
    status: '',
    data: []
  },
  regions: {
    status: '',
    data: []
  }
} as InitState;

export const getRegions = createAsyncThunk('userLocationData/getRegions', async (countryIso2: string, { dispatch }) => {
  try {
    let regions: Region[] = [];
    let oDataCount = 0;

    do {
      const { data } = await getRegionsValues(regions.length, countryIso2);
      regions = regions.concat(data.value);
      oDataCount = data['@odata.count'];
    } while (regions.length < oDataCount);

    return regions;
  } catch (error) {
    dispatch(errorCatch({ place: 'userLocationData/getRegions.rejected', data: error }));
    return Promise.reject(error);
  }
});

const getRegionsValues = async ($skip: number, countryIso2: string) =>
  Axios.get<RegionsDataType>('/api/v2/Location/states', {
    params: {
      $filter: `countryIso2 eq '${countryIso2}'`,
      $skip,
      $orderby: 'name'
    }
  });

export const getCountries = createAsyncThunk<CountriesDataType>('userLocationData/getCountries', (_, { dispatch }) => {
  try {
    return JSON.parse(JSON.stringify(countries));
  } catch (error) {
    dispatch(errorCatch({ place: 'userLocationData/getCountries.rejected', data: error }));
    return Promise.reject(error);
  }
});

const slice = createSlice({
  name: 'userLocationData',
  initialState,
  reducers: {},
  extraReducers: {
    // getRegions
    [getRegions.pending.toString()]: state => {
      state.regions.status = 'loading';
    },
    [getRegions.fulfilled.toString()]: (state, action) => {
      state.regions.data = action.payload;
      state.regions.status = 'success';
    },
    [getRegions.rejected.toString()]: state => {
      state.regions.status = 'rejected';
    },
    // getCountries
    [getCountries.pending.toString()]: state => {
      state.countries.status = 'loading';
    },
    [getCountries.fulfilled.toString()]: (state, action) => {
      state.countries.data = action.payload;
      state.countries.status = 'success';
    },
    [getCountries.rejected.toString()]: state => {
      state.countries.status = 'rejected';
    }
  }
});

export default slice.reducer;
